import { ProposalTemplateTag } from '@airship-platform/airship-proposal';
import {
  ProposalTemplate,
  ProposalTemplateOption,
} from './proposalTemplate.models';

export interface ProposalTemplateState {
  proposalTemplateOptionIds: string[];
  proposalTemplateOptionEntities: Record<string, ProposalTemplateOption>;
  proposalTemplateIds: string[];
  proposalTemplateEntities: Record<string, ProposalTemplate>;
  tags: ProposalTemplateTag[];
  error: string | null;
  isLoadedOptions: boolean;
  isLoadingOptions: boolean;
  isLoadedTemplates: boolean;
  isLoadingTemplates: boolean;
  isLoadedTags: boolean;
  isLoadingTags: boolean;
}

export const initState: ProposalTemplateState = {
  proposalTemplateOptionIds: [],
  proposalTemplateOptionEntities: {},
  proposalTemplateIds: [],
  proposalTemplateEntities: {},
  tags: [],
  error: null,
  isLoadedOptions: false,
  isLoadingOptions: true,
  isLoadedTemplates: false,
  isLoadingTemplates: true,
  isLoadedTags: false,
  isLoadingTags: true,
};

type Action =
  | {
      type: 'SET_LOADING_STATE';
      payload: Partial<
        Pick<
          ProposalTemplateState,
          | 'isLoadingOptions'
          | 'isLoadedOptions'
          | 'isLoadingTemplates'
          | 'isLoadedTemplates'
          | 'isLoadingTags'
          | 'isLoadedTags'
        >
      >;
    }
  | {
      type: 'QUERY_PROPOSAL_TEMPLATES';
    }
  | {
      type: 'QUERY_PROPOSAL_TEMPLATES_SUCCESS';
      payload: { proposalTemplates: ProposalTemplate[] };
    }
  | {
      type: 'QUERY_PROPOSAL_TEMPLATES_FAILURE';
      payload: { error: string };
    }
  | {
      type: 'QUERY_PROPOSAL_TEMPLATE_OPTIONS';
    }
  | {
      type: 'QUERY_PROPOSAL_TEMPLATE_OPTIONS_SUCCESS';
      payload: { proposalTemplateOptions: ProposalTemplateOption[] };
    }
  | {
      type: 'QUERY_PROPOSAL_TEMPLATE_OPTIONS_FAILURE';
      payload: { error: string };
    }
  | {
      type: 'FETCH_PROPOSAL_TEMPLATE_TAGS';
    }
  | {
      type: 'FETCH_PROPOSAL_TEMPLATE_TAGS_SUCCESS';
      payload: { tags: ProposalTemplateTag[] };
    }
  | {
      type: 'FETCH_PROPOSAL_TEMPLATE_TAGS_FAILURE';
      payload: { error: string };
    };

export const proposalTemplatesReducer = (
  state: ProposalTemplateState,
  action: Action
): ProposalTemplateState => {
  switch (action.type) {
    case 'SET_LOADING_STATE':
      return {
        ...state,
        ...action.payload,
        error: null,
      };

    case 'QUERY_PROPOSAL_TEMPLATE_OPTIONS':
      return {
        ...state,
        error: null,
        isLoadingOptions: true,
        isLoadedOptions: false,
      };

    case 'QUERY_PROPOSAL_TEMPLATE_OPTIONS_SUCCESS': {
      return {
        ...state,
        isLoadingOptions: false,
        isLoadedOptions: true,
        proposalTemplateOptionIds: [
          ...new Set([
            ...state.proposalTemplateOptionIds,
            ...action.payload.proposalTemplateOptions.map(
              (a) => a.proposalTemplateOptionId
            ),
          ]),
        ],
        proposalTemplateOptionEntities:
          action.payload.proposalTemplateOptions.reduce(
            (prev, curr) => ({
              ...prev,
              [curr.proposalTemplateOptionId]: curr,
            }),
            state.proposalTemplateOptionEntities
          ),
      };
    }

    case 'QUERY_PROPOSAL_TEMPLATES':
      return {
        ...state,
        error: null,
        isLoadingTemplates: true,
        isLoadedTemplates: false,
      };

    case 'QUERY_PROPOSAL_TEMPLATES_SUCCESS': {
      return {
        ...state,
        isLoadingTemplates: false,
        isLoadedTemplates: true,
        proposalTemplateIds: [
          ...new Set([
            ...state.proposalTemplateIds,
            ...action.payload.proposalTemplates.map(
              (a) => a.proposalTemplateId
            ),
          ]),
        ],
        proposalTemplateEntities: action.payload.proposalTemplates.reduce(
          (prev, curr) => ({ ...prev, [curr.proposalTemplateId]: curr }),
          state.proposalTemplateEntities
        ),
      };
    }

    case 'FETCH_PROPOSAL_TEMPLATE_TAGS':
      return {
        ...state,
        error: null,
        isLoadingTags: true,
        isLoadedTags: false,
      };

    case 'FETCH_PROPOSAL_TEMPLATE_TAGS_SUCCESS': {
      return {
        ...state,
        isLoadingTags: false,
        isLoadedTags: true,
        tags: action.payload.tags,
      };
    }

    case 'QUERY_PROPOSAL_TEMPLATES_FAILURE':
    case 'QUERY_PROPOSAL_TEMPLATE_OPTIONS_FAILURE':
    case 'FETCH_PROPOSAL_TEMPLATE_TAGS_FAILURE':
      return {
        ...state,
        isLoadingTags: false,
        isLoadedTags: true,
        isLoadedOptions:
          action.type === 'QUERY_PROPOSAL_TEMPLATE_OPTIONS_FAILURE',
        isLoadingOptions: false,
        isLoadedTemplates: action.type === 'QUERY_PROPOSAL_TEMPLATES_FAILURE',
        isLoadingTemplates: false,
        error: action.payload.error,
      };

    default:
      return state;
  }
};
